import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { FormControlLabel, Switch, Typography, Grid, Box, Divider, Tooltip, Checkbox, MenuItem, Select } from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from '../../../../../reusable-components/iconify';
import { cloudinaryThumbnail } from '../../../../../utils/formatCloudinary';
import { fCurrency } from '../../../../../utils/formatNumber';
import { useDispatch, useSelector } from '../../../../../redux/store';
import Image from '../../../../../reusable-components/image';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import { getAllCartsItemsCount } from '../../../../../redux/slices/orderSheet';

import {
  addItemToCart,
  addItemToPunchoutCart,
  removeItemFromCart,
  clearBadgeFromRedux,
  updateSelectedItem,
  getCartsForUser,
  getCategoryData,
  updateShortLongTerm,
  updateCartItemPricingType,
  updatePunchoutCartItemSubcategory,
  removePunchoutItemFromCart,
  updatePunchoutShortLongTerm
} from '../../../../../redux/slices/rentals';

CartItemView.propTypes = {
  cartItem: PropTypes.object.isRequired,
  key: PropTypes.number,
  showDivider: PropTypes.bool,
  showCartEditOptions: PropTypes.bool
};

export default function CartItemView({ cartItem, key, showDivider, showCartEditOptions }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const {
    data: { categoryData },
  } = useSelector((state) => state.rentals);
  const [checked, setChecked] = useState(true);
  const [pricingTypeValue, setPricingTypeValue] = useState(cartItem.pricingTypeId);
  const [subcategoryValue, setSubcategoryValue] = useState(cartItem.subcategoryId);
  const pricingTypes = cartItem.pricingTypes ?? [];

  useEffect(() => {
    setPricingTypeValue(cartItem.pricingTypeId);
    setSubcategoryValue(cartItem.subcategoryId);
  }, [cartItem.pricingTypeId, cartItem.subcategoryId]);

  useEffect(() => {
    if (!cartItem.isPunchoutCart) return;
    if (categoryData.length === 0) dispatch(getCategoryData());
  }, [cartItem.isPunchoutCart, categoryData.length]);

  const CustomIconButton = styled(Iconify)({
    cursor: 'pointer',
    color: '#fff',
  });

  const handleChange = (event, isSelected) => {
    dispatch(updateSelectedItem(cartItem, isSelected));
  };
  const handleEditSortLongTerm = () => {
    setChecked((prev) => !prev);
    if(!cartItem.isPunchoutCart)
    dispatch(updateShortLongTerm(cartItem.cartItemId));
    else dispatch(updatePunchoutShortLongTerm(cartItem.cartItemId));
    dispatch(getCartsForUser());
  };
  const handleAddCart = (cartItem) => {
    if(!cartItem.isPunchoutCart) 
    dispatch(addItemToCart(cartItem.itemId, cartItem.residentId, user.id, cartItem.cartId)).then(() => dispatch(getAllCartsItemsCount()));
    else dispatch(addItemToPunchoutCart(cartItem.cartItemId, cartItem.residentId, user.id, cartItem.cartId)).then(() => dispatch(getAllCartsItemsCount()));
    dispatch(clearBadgeFromRedux());
  };
  const handleRemoveCart = (item, deleteAll) => {
    if(!cartItem.isPunchoutCart) 
    dispatch(removeItemFromCart(item.cartItemId, deleteAll)).then(() => dispatch(getAllCartsItemsCount()));
    else dispatch(removePunchoutItemFromCart(item.cartItemId, deleteAll)).then(() => dispatch(getAllCartsItemsCount()));
    dispatch(clearBadgeFromRedux());
  };
  const punchoutSubcategories = categoryData.filter((subcategory) =>
    subcategory.categoryId === cartItem.categoryId && subcategory.subcategoryId
  );

  return (
    <>
      <Box key={key}>
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {showCartEditOptions && (
            <Checkbox
              checked={cartItem.isSelected}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
              color="secondary"
            />
          )}
          <Image
            alt="attachment"
            src={
              cartItem.image ||
              cloudinaryThumbnail(
                'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
              )
            }
            sx={{ borderRadius: 1.5, width: 120, maxHeight: '120px', mr: 4, ml: 4 }}
          />
          <Grid container direction="column" spacing={1} alignItems="flex-start" sx={{ flex: 1, ml: 4 }}>
            <Grid item>
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#707070',
                    overflow: 'hidden',
                    maxWidth: '250px',
                    mr: 4,
                    ml: 0,
                  }}
                >
                  {cartItem.description}
                </Typography>
                <Typography variant="subtitle2" sx={{ ml: 2, color: '#6d6e71' }}>
                  {cartItem.vendorName}
                </Typography>
                <Typography variant="subtitle2" sx={{ ml: 2, color: '#6d6e71' }}>
                  {cartItem.facilityName}
                </Typography>
                <Typography variant="subtitle2" sx={{ ml: 2, color: '#6d6e71' }}>
                  {cartItem.vendorItemId}
                </Typography>
              </Box>
            </Grid>
            {showCartEditOptions ? (
              <Grid item sx={{ marginRight: 'auto' }}>
                <Grid container direction="row" spacing={1} alignItems="center">
                  <Grid item>
                    <div style={{ padding: '20px' }}>
                      <QuantityChange>
                        <CustomIconButton
                          icon="ic:round-minus"
                          width={12}
                          height={12}
                          onClick={() => handleRemoveCart(cartItem, false)}
                        />
                        <span>&nbsp;&nbsp;</span>
                        <span
                          style={{
                            color: '#8dc63f',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                            border: '1px solid #8cc53f',
                            width: '25px',
                            height: '22px',
                            fontSize: '14px',
                          }}
                        >
                          {cartItem.quantity}
                        </span>
                        <span>&nbsp;&nbsp;</span>
                        <CustomIconButton
                          icon="iconamoon:sign-plus-bold"
                          width={12}
                          height={12}
                          onClick={() => handleAddCart(cartItem)}
                        />
                      </QuantityChange>
                    </div>
                  </Grid>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{
                      opacity: 1,
                    }}
                  />
                  <Grid item>
                    <FormControlLabel
                      label="Long Term"
                      control={
                        <Switch
                          sx={{ mt: 0, ml: 1 }}
                          checked={checked}
                          size="small"
                          disabled={!showCartEditOptions}
                          onClick={() => handleEditSortLongTerm()}
                        />
                      }
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />{' '}
                  </Grid>{' '}
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{
                      opacity: 1,
                    }}
                  />
                  <Grid item>
                    {cartItem.isPunchoutCart ? (
                      <Typography disabled sx={{ minWidth: 170, fontSize: 12, marginLeft: 2 }}>
                        ${cartItem.price.toFixed(2)} / {cartItem.pricingTypeName}
                      </Typography>
                    ) : pricingTypes.length === 0 ? (
                      <Typography disabled sx={{ minWidth: 170, fontSize: 12, marginLeft: 2 }}>
                        ${cartItem.price.toFixed(2)} / {cartItem.pricingTypeName}
                      </Typography>
                    ) : (
                      <Select
                        size="small"
                        value={pricingTypeValue}
                        onChange={(e) => {
                          setPricingTypeValue(e.target.value);
                          dispatch(updateCartItemPricingType(cartItem.cartItemId, e.target.value));
                        }}
                        sx={{ minWidth: 170, fontSize: 12 }}
                        displayEmpty
                      >
                        {pricingTypes.map((type) => (
                          <MenuItem key={type.pricingType} value={type.pricingType}>
                            {fCurrency(type.price)} / {type.pricingTypeName}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Grid>
                  {cartItem.isPunchoutCart && (
                    <>
                      <Divider orientation="vertical" variant="middle" flexItem sx={{ opacity: 1 }} />
                      <Grid item>
                        <Select
                          size="small"
                          value={subcategoryValue}
                          onChange={(e) => {
                            setSubcategoryValue(e.target.value);
                            dispatch(updatePunchoutCartItemSubcategory(cartItem.cartItemId, e.target.value));
                          }}
                          sx={{ minWidth: 170, fontSize: 12 }}
                          displayEmpty
                        >
                          <MenuItem value={0} disabled>Select subcategory</MenuItem>
                          {punchoutSubcategories.map((subcategory) => (
                            <MenuItem key={subcategory.subcategoryId} value={subcategory.subcategoryId}>
                              {subcategory.subcategoryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </>
                  )}
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{
                      opacity: 1,
                    }}
                  />
                  <Grid item>
                    <Tooltip title="Delete">
                      <ConfirmDialog
                          icon="delete-outline"
                          title="Delete"
                          color="error"
                          content={`Are you sure you want to remove this item from your cart?`}
                          actionButton="Delete"
                          action={() => handleRemoveCart(cartItem, true)}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item> </Grid>
                  {''}
                </Grid>
              </Grid>
            ) : (
              <div>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#707070',
                    overflow: 'hidden',
                    maxWidth: '250px',
                    ml: 1,
                    fontSize: '12px',
                  }}
                >
                  Quantity: {cartItem.quantity}/$ {cartItem.price} each
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#707070',
                    overflow: 'hidden',
                    maxWidth: '250px',
                    ml: 1,
                    fontSize: '12px',
                  }}
                >
                  {cartItem.longTerm ? 'Long term' : 'Short term'}
                </Typography>
              </div>
            )}
          </Grid>
          <Typography
            sx={{
              color: '#8cc53f',
              fontSize: '15px',
              fontWeight: 'bold',
              padding: 4,
              marginLeft: 'auto',
            }}
          >
            ${(cartItem.price * cartItem.quantity).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      {showDivider && <Divider sx={{ mb: 2, mt: 2, width: '85%', ml: 4, opacity: 3, backgroundColor: '#707070' }} />}
    </>
  );
}

const QuantityChange = styled('div')(({ theme }) => ({
  height: 22,
  backgroundColor: '#8dc63f',
  color: '#8dc63f',
  padding: theme.spacing(0.5),
  margin: '2.5px 3px 2.5px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '2px',
}));
